import React, { useState, useEffect } from "react";
import axios from "axios";
import { MapPin, Briefcase, Euro, ArrowRight } from "lucide-react";
import { JOBS } from "../mock/mock";
import useInView from "../hooks/useInView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Jobs({ onApply }) {
  const [ref, inView] = useInView();
  const [jobs, setJobs] = useState(JOBS);
  const [filter, setFilter] = useState("Tous");

  useEffect(() => {
    axios
      .get(`${API}/jobs`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) setJobs(res.data);
      })
      .catch(() => {
        // fallback to mock JOBS on error
      });
  }, []);

  const domains = ["Tous", ...new Set(jobs.map((j) => j.domain))];
  const filtered =
    filter === "Tous" ? jobs : jobs.filter((j) => j.domain === filter);

  return (
    <section id="jobs" className="relative bg-[#0c1a2c] py-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-bold tracking-[0.2em] text-[#F5941E]">
            NOS OFFRES
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
            Offres d'emploi - CDI
          </h2>
          <p className="mt-4 text-slate-400">
            Découvrez nos opportunités dans l'industrie en Bretagne et Pays de la
            Loire. 100% CDI.
          </p>
        </div>

        {/* Filters */}
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          {domains.map((d) => (
            <button
              key={d}
              onClick={() => setFilter(d)}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-all duration-300 ${
                filter === d
                  ? "bg-[#F5941E] text-white"
                  : "border border-white/15 bg-white/5 text-slate-300 hover:bg-white/10"
              }`}
            >
              {d}
            </button>
          ))}
        </div>

        {/* Job grid */}
        <div ref={ref} className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((job, i) => (
            <div
              key={job.id}
              className={`group flex flex-col rounded-3xl border border-white/10 bg-white/[0.03] p-7 transition-all duration-500 hover:border-[#F5941E]/40 hover:-translate-y-1 ${
                inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className="flex items-center justify-between">
                <span className="rounded-full bg-[#F5941E]/15 px-3 py-1 text-xs font-semibold text-[#F5941E]">
                  {job.domain}
                </span>
                <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-400">
                  {job.contract}
                </span>
              </div>

              <h3 className="mt-4 text-lg font-bold text-white leading-snug">
                {job.title}
              </h3>

              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-400">
                <span className="flex items-center gap-1.5">
                  <MapPin size={14} className="text-[#F5941E]" />
                  {job.location}
                </span>
                <span className="flex items-center gap-1.5">
                  <Euro size={14} className="text-[#F5941E]" />
                  {job.salary}
                </span>
              </div>

              <p className="mt-4 text-sm leading-relaxed text-slate-400 flex-1">
                {job.description}
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                {job.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-md bg-white/5 px-2.5 py-1 text-xs text-slate-300"
                  >
                    {t}
                  </span>
                ))}
              </div>

              <button
                onClick={() => onApply(job)}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full border border-[#F5941E]/40 bg-[#F5941E]/10 px-5 py-3 text-sm font-semibold text-[#F5941E] transition-all duration-300 group-hover:bg-[#F5941E] group-hover:text-white"
              >
                Postuler
                <ArrowRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-12 flex items-center justify-center gap-2 text-sm text-slate-400">
          <Briefcase size={16} className="text-[#F5941E]" />
          Vous ne trouvez pas votre poste ? Envoyez-nous une candidature
          spontanée.
        </div>
      </div>
    </section>
  );
}
